<template>
  <div class=" card">
    <!-- Header -->
    <div class="card-header d-flex align-items-center justify-content-between">
      <h4 class="card-title mb-0">
        <i class="bi bi-images me-2"></i>Gallery Management
      </h4>
      <div class="d-flex gap-2">
        <button class="new-album-btn" @click="openCreateModal">
          <i class="bi bi-plus-lg me-1"></i> New Album
        </button>
      </div>
    </div>

    <!-- Create Album Modal -->
    <div v-if="showCreateForm" class="create-modal" @click="handleCreateBackdropClick">
      <div class="create-content" @click.stop>
        <div class="create-header">
          <h3>Create New Album</h3>
          <button type="button" class="close-btn" @click="cancelCreate" :disabled="creating" aria-label="Close create album dialog">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="create-body">
          <div class="form-group">
            <input
              type="text"
              v-model="newAlbum.title"
              placeholder="Album name"
              class="album-input"
              @keyup.enter="createAlbum"
            >
          </div>
          <div class="form-group">
            <textarea
              v-model="newAlbum.description"
              placeholder="Optional description..."
              class="album-textarea"
              rows="3"
            ></textarea>
          </div>
          <div class="form-group">
            <label class="form-label d-block mb-2">Cover Image (optional)</label>
            <input
              type="file"
              accept="image/*"
              class="album-input"
              @change="handleCoverImageChange($event, 'new')"
              ref="newCoverImageInput"
            >
            <div v-if="newAlbum.cover_image_preview" class="cover-preview mt-2">
              <img :src="newAlbum.cover_image_preview" alt="New album cover preview" class="cover-preview-image">
              <button type="button" class="btn-secondary btn-sm mt-2" @click="clearCoverImage('new')">Remove Cover</button>
            </div>
          </div>
          <div class="form-group album-public-toggle">
            <div class="form-check">
              <input
                class="form-check-input"
                id="newAlbumPublic"
                type="checkbox"
                v-model="newAlbum.is_public"
              >
              <label class="form-check-label" for="newAlbumPublic">Make this album public</label>
            </div>
          </div>
        </div>

        <div class="form-actions create-actions">
          <button
            class="btn-primary"
            @click="createAlbum"
            :disabled="!newAlbum.title.trim() || creating"
            style="background: #009CAD"
          >
            {{ creating ? 'Creating...' : 'Create Album' }}
          </button>
          <button class="btn-secondary" @click="cancelCreate" :disabled="creating">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading albums...</p>
    </div>

    <!-- Albums Grid -->
    <div v-else-if="albums.length > 0" class="albums-grid card-body">
      <div
        v-for="album in albums"
        :key="album.id"
        class="album-card"
        @click="selectAlbum(album)"
      >
        <div class="album-thumbnail">
          <img
            :src="getAlbumThumbnail(album)"
            :alt="album.title"
            @error="handleImageError"
          >
          <div class="album-overlay">
            <button class="upload-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7,10 12,15 17,10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Manage
            </button>
          </div>
        </div>
        <div class="album-info">
          <h3 class="album-title">{{ album.title }}</h3>
          <div class="album-meta">
            <span>{{ formatDate(album.created_at) }}</span>
            <span>{{ getItemCount(album) }} items</span>
            <span v-if="album.is_public" class="public-badge">Public</span>
            <span v-else class="private-badge">Private</span>
          </div>
        </div>
        <button class="album-menu" @click.stop="toggleMenu(album.id)">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="1"/>
            <circle cx="12" cy="5" r="1"/>
            <circle cx="12" cy="19" r="1"/>
          </svg>
        </button>

        <!-- Menu Dropdown -->
        <div v-if="activeMenu === album.id" class="album-dropdown" @click.stop>
          <button @click="editAlbum(album)">Edit</button>
          <button @click="confirmDeleteAlbum(album)" class="delete-option">Delete</button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="empty-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21,15 16,10 5,21"/>
        </svg>
      </div>
      <h3>No albums yet</h3>
      <p>Create your first album to start organizing your photos and videos</p>
      <button class="btn-primary" @click="openCreateModal">
        Create Album
      </button>
    </div>

    <!-- Album Management Modal -->
    <div v-if="selectedAlbum" class="album-modal" @click.stop>
      <div class="album-content" @click.stop>
        <div class="album-header">
          <div class="album-title-section">
            <h2>{{ selectedAlbum.title }}</h2>
            <div class="album-meta-info">
              <span>{{ getItemCount(selectedAlbum) }} items</span>
              <span v-if="selectedAlbum.is_public" class="public-badge">Public</span>
              <span v-else class="private-badge">Private</span>
            </div>
          </div>
          <button class="close-btn" @click="closeAlbum">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Tab Navigation -->
        <div class="tab-navigation">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'items' }"
            @click="activeTab = 'items'"
          >
            Items ({{ albumItems.length }})
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'upload' }"
            @click="activeTab = 'upload'"
          >
            Upload Files
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'youtube' }"
            @click="activeTab = 'youtube'"
          >
            Add YouTube
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'vimeo' }"
            @click="activeTab = 'vimeo'"
          >
            Add Vimeo
          </button>
        </div>

        <!-- Items Tab -->
        <div v-if="activeTab === 'items'" class="items-tab">
          <div v-if="loadingItems" class="loading-items">
            <div class="spinner-small"></div>
            <p>Loading items...</p>
          </div>

          <div v-else-if="albumItems.length === 0" class="empty-items">
            <p>No items in this album yet</p>
            <div class="empty-actions">
              <button class="btn-primary" @click="activeTab = 'upload'">
                Upload Files
              </button>
              <button class="btn-secondary" @click="activeTab = 'youtube'">
                Add YouTube Video
              </button>
              <button class="btn-secondary" @click="activeTab = 'vimeo'">
                Add Vimeo Video
              </button>
            </div>
          </div>

          <div v-else class="items-grid">
            <div
              v-for="item in albumItems"
              :key="item.id"
              class="item-card"
            >
              <div class="item-image">
                <img
                  :src="getItemThumbnail(item)"
                  :alt="item.title"
                  @error="handleImageError"
                >
                <div class="item-overlay">
                  <button
                    class="item-action-btn delete-btn"
                    @click="confirmDeleteItem(item)"
                    title="Delete item"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="3,6 5,6 21,6"/>
                      <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div class="item-info">
                <span class="item-title">{{ item.title }}</span>
                <div class="item-meta">
                  <span class="item-type" :class="item.type">{{ item.type }}</span>
                  <span class="item-date">{{ formatDate(item.created_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Upload Tab -->
        <div v-if="activeTab === 'upload'" class="upload-tab">
          <MultiImageUploader
            :accept="galleryAcceptedTypes"
            :max-file-size-m-b="50"
            :allow-folders="true"
            :auto-upload="false"
            title="Drag photos, videos or a whole folder here, or click to browse"
            :upload-handler="handleGalleryFileUpload"
            :item-defaults="(file) => ({ title: titleFromFilename(file.name) })"
            @batch-complete="handleGalleryBatchComplete"
          >
            <template #item-extra="{ item, disabled }">
              <input
                type="text"
                class="gallery-item-title-input"
                v-model="item.title"
                :disabled="disabled"
                placeholder="Title"
                aria-label="Item title"
              >
            </template>
          </MultiImageUploader>
        </div>

        <!-- YouTube Tab -->
        <div v-if="activeTab === 'youtube'" class="youtube-tab">
          <div class="youtube-form">
            <h3>Add YouTube Video</h3>

            <div class="form-group">
              <label>YouTube URL *</label>
              <input
                type="url"
                v-model="youtubeVideo.youtube_url"
                placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                class="album-input"
                @input="extractYouTubeInfo"
              >
            </div>

            <div class="form-group">
              <label>Video Title *</label>
              <input
                type="text"
                v-model="youtubeVideo.title"
                placeholder="Enter video title"
                class="album-input"
              >
            </div>

            <div class="form-group">
              <label>Description (Optional)</label>
              <textarea
                v-model="youtubeVideo.description"
                placeholder="Enter video description..."
                class="album-textarea"
                rows="3"
              ></textarea>
            </div>

            <!-- YouTube Preview -->
            <div v-if="youtubePreview.videoId" class="youtube-preview">
              <h4>Preview</h4>
              <div class="preview-container">
                <img
                  :src="youtubePreview.thumbnail"
                  :alt="youtubeVideo.title"
                  class="preview-thumbnail"
                >
                <div class="preview-info">
                  <h5>{{ youtubeVideo.title || 'Untitled Video' }}</h5>
                  <p>Video ID: {{ youtubePreview.videoId }}</p>
                </div>
              </div>
            </div>

            <div class="form-actions">
              <button
                class="btn-primary"
                @click="addYouTubeVideo"
                :disabled="!canAddYouTubeVideo || addingYouTube"
                style="background: #009CAD"
              >
                {{ addingYouTube ? 'Adding...' : 'Add Video' }}
              </button>
              <button class="btn-secondary" @click="resetYouTubeForm">
                Reset
              </button>
            </div>
          </div>
        </div>

        <!-- Vimeo Tab -->
        <div v-if="activeTab === 'vimeo'" class="youtube-tab">
          <div class="youtube-form">
            <h3>Add Vimeo Video</h3>

            <div class="form-group">
              <label>Vimeo URL *</label>
              <input
                type="url"
                v-model="vimeoVideo.vimeo_url"
                placeholder="https://vimeo.com/123456789"
                class="album-input"
                @input="extractVimeoInfo"
              >
            </div>

            <div class="form-group">
              <label>Video Title *</label>
              <input
                type="text"
                v-model="vimeoVideo.title"
                placeholder="Enter video title"
                class="album-input"
              >
            </div>

            <div class="form-group">
              <label>Description (Optional)</label>
              <textarea
                v-model="vimeoVideo.description"
                placeholder="Enter video description..."
                class="album-textarea"
                rows="3"
              ></textarea>
            </div>

            <!-- Vimeo Preview -->
            <div v-if="vimeoPreview.videoId" class="youtube-preview">
              <h4>Preview</h4>
              <div class="preview-container">
                <!-- Optional thumbnail: you can wire this up to a thumbnail service later -->
                <div class="preview-info">
                  <h5>{{ vimeoVideo.title || 'Untitled Vimeo Video' }}</h5>
                  <p>Vimeo ID: {{ vimeoPreview.videoId }}</p>
                </div>
              </div>
            </div>

            <div class="form-actions">
              <button
                class="btn-primary"
                @click="addVimeoVideo"
                :disabled="!canAddVimeoVideo || addingVimeo"
                style="background: #009CAD"
              >
                {{ addingVimeo ? 'Adding...' : 'Add Video' }}
              </button>
              <button class="btn-secondary" @click="resetVimeoForm">
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div v-if="editingAlbum" class="edit-modal" @click.stop>
      <div class="edit-content">
        <h2>Edit Album</h2>
        <div class="form-group">
          <input
            type="text"
            v-model="editingAlbum.title"
            placeholder="Album title"
            class="album-input"
          >
        </div>
        <div class="form-group">
          <textarea
            v-model="editingAlbum.description"
            placeholder="Description"
            class="album-textarea"
            rows="3"
          ></textarea>
        </div>
        <div class="form-group">
          <label class="form-label d-block mb-2">Cover Image (optional)</label>
          <input
            type="file"
            accept="image/*"
            class="album-input"
            @change="handleCoverImageChange($event, 'edit')"
            ref="editCoverImageInput"
          >
          <div v-if="editingAlbum.cover_image_preview" class="cover-preview mt-2">
            <img :src="editingAlbum.cover_image_preview" alt="Edit album cover preview" class="cover-preview-image">
            <button type="button" class="btn-secondary btn-sm mt-2" @click="clearCoverImage('edit')">Remove Cover</button>
          </div>
        </div>
        <div class="form-group album-public-toggle">
          <div class="form-check">
            <input
              class="form-check-input"
              id="editAlbumPublic"
              type="checkbox"
              v-model="editingAlbum.is_public"
            >
            <label class="form-check-label" for="editAlbumPublic">Make this album public</label>
          </div>
        </div>
        <div class="form-actions">
          <button class="btn-primary" @click="saveEdit" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
          <button class="btn-secondary" @click="closeEdit">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Integrated Confirmation Dialog -->
    <div v-if="confirmDialog.isVisible" class="confirmation-modal" @click="handleConfirmBackdropClick">
      <div class="confirmation-dialog" @click.stop>
        <div class="confirmation-header">
          <h3 class="confirmation-title">{{ confirmDialog.title }}</h3>
          <button class="confirmation-close" @click="cancelConfirm" :disabled="confirmDialog.isLoading">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="confirmation-content">
          <div class="confirmation-icon" :class="confirmDialog.type">
            <svg v-if="confirmDialog.type === 'danger'" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <svg v-else-if="confirmDialog.type === 'warning'" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9,11L12,14L22,4"/>
            </svg>
          </div>
          <p class="confirmation-message">{{ confirmDialog.message }}</p>
          <p v-if="confirmDialog.detail" class="confirmation-detail">{{ confirmDialog.detail }}</p>
        </div>

        <div class="confirmation-actions">
          <button
            class="btn-secondary confirmation-cancel"
            @click="cancelConfirm"
            :disabled="confirmDialog.isLoading"
          >
            {{ confirmDialog.cancelText }}
          </button>
          <button
            class="btn-primary confirmation-confirm"
            :class="{ 'btn-danger': confirmDialog.type === 'danger', 'btn-warning': confirmDialog.type === 'warning' }"
            @click="executeConfirm"
            :disabled="confirmDialog.isLoading"
          >
            <span v-if="confirmDialog.isLoading" class="button-spinner"></span>
            {{ confirmDialog.isLoading ? 'Processing...' : confirmDialog.confirmText }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast Messages -->
    <div v-if="message" class="toast" :class="messageType">
      {{ message }}
    </div>

    <!-- Cover Image Cropper -->
    <CoverImageCropper
      v-model="showCoverCropper"
      :imageSrc="coverCropperSrc"
      @cropped="handleCoverCropped"
      @cancel="handleCoverCropperCancel"
    />
  </div>
</template>

<script>
import GalleryApiService from '@/services/gallery/GalleryApiService'
import CoverImageCropper from '@/components/common/CoverImageCropper.vue'
import MultiImageUploader from '@/components/common/MultiImageUploader.vue'

export default {
  name: 'SimpleGalleryUpload',

  components: { CoverImageCropper, MultiImageUploader },

  data() {
    return {
      albums: [],
      albumItems: [],
      activeTab: 'items',

      // Form states
      showCreateForm: false,
      newAlbum: {
        title: '',
        description: '',
        is_public: true,
        cover_image: null,
        cover_image_preview: null
      },
      editingAlbum: null,

      // Upload states
      selectedAlbum: null,

      // YouTube states
      youtubeVideo: {
        title: '',
        description: '',
        youtube_url: ''
      },
      youtubePreview: {
        videoId: null,
        thumbnail: null
      },
      addingYouTube: false,

      // UI states
      loading: false,
      loadingItems: false,
      creating: false,
      saving: false,
      activeMenu: null,
      message: '',
      messageType: 'success',

      // Confirmation Dialog State
      confirmDialog: {
        isVisible: false,
        title: 'Confirm Action',
        message: 'Are you sure you want to continue?',
        detail: '',
        type: 'info', // 'info', 'warning', 'danger'
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        isLoading: false,
        onConfirm: null,
        onCancel: null
      },
      // Vimeo states
      vimeoVideo: {
        title: '',
        description: '',
        vimeo_url: ''
      },
      vimeoPreview: {
        videoId: null
      },
      addingVimeo: false,

      // Cover image cropper state
      showCoverCropper: false,
      coverCropperSrc: null,
      coverCropperMode: null,

      // Default thumbnail
      defaultThumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjhmOWZhIi8+CiAgPGNpcmNsZSBjeD0iMTAwIiBjeT0iODAiIHI9IjIwIiBmaWxsPSIjZGVlMmU2Ii8+CiAgPHBvbHlnb24gcG9pbnRzPSI2MCwxNDAgMTQwLDE0MCAyMDAsMTAwIDI0MCwxNDAiIGZpbGw9IiNkZWUyZTYiLz4KPC9zdmc+'
    }
  },

  computed: {
    canAddYouTubeVideo() {
      return this.youtubeVideo.title.trim() &&
        this.youtubeVideo.youtube_url.trim() &&
        GalleryApiService.isValidYouTubeUrl(this.youtubeVideo.youtube_url)
    },

    canAddVimeoVideo() {
      return this.vimeoVideo.title.trim() &&
        this.vimeoVideo.vimeo_url.trim() &&
        GalleryApiService.isValidVimeoUrl(this.vimeoVideo.vimeo_url)
    },

    // Mirrors GalleryApiService.validateFileData()'s allowed mime types, so
    // the uploader's client-side type check matches what the backend (and
    // the rest of this page) already enforce for gallery items.
    galleryAcceptedTypes() {
      return [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
        'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv'
      ]
    }
  },

  mounted() {
    this.loadAlbums()
    document.addEventListener('click', this.closeMenus)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.closeMenus)
  },

  methods: {
    openCreateModal() {
      this.showCreateForm = true
    },

    handleCreateBackdropClick() {
      if (!this.creating) {
        this.cancelCreate()
      }
    },

    // Confirmation Dialog Methods
    showConfirmDialog(options = {}) {
      return new Promise((resolve) => {
        this.confirmDialog = {
          isVisible: true,
          title: options.title || 'Confirm Action',
          message: options.message || 'Are you sure you want to continue?',
          detail: options.detail || '',
          type: options.type || 'info',
          confirmText: options.confirmText || 'Confirm',
          cancelText: options.cancelText || 'Cancel',
          isLoading: false,
          onConfirm: options.onConfirm || null,
          onCancel: options.onCancel || null,
          resolve: resolve
        }
      })
    },

    async executeConfirm() {
      if (this.confirmDialog.onConfirm) {
        try {
          this.confirmDialog.isLoading = true
          await this.confirmDialog.onConfirm()
          this.confirmDialog.isVisible = false
          this.confirmDialog.resolve(true)
        } catch (error) {
          this.confirmDialog.isLoading = false
          throw error
        }
      } else {
        this.confirmDialog.isVisible = false
        this.confirmDialog.resolve(true)
      }
    },

    cancelConfirm() {
      if (!this.confirmDialog.isLoading) {
        this.confirmDialog.isVisible = false
        if (this.confirmDialog.onCancel) {
          this.confirmDialog.onCancel()
        }
        this.confirmDialog.resolve(false)
      }
    },

    handleConfirmBackdropClick() {
      if (!this.confirmDialog.isLoading) {
        this.cancelConfirm()
      }
    },

    // Updated Delete Methods with Confirmation Dialog
    async confirmDeleteAlbum(album) {
      this.activeMenu = null // Close the menu first

      try {
        const confirmed = await this.showConfirmDialog({
          type: 'danger',
          title: 'Delete Album',
          message: `Are you sure you want to delete "${album.title}"?`,
          detail: 'This action cannot be undone. All items in this album will also be deleted.',
          confirmText: 'Delete Album',
          cancelText: 'Cancel',
          onConfirm: async () => {
            await GalleryApiService.deleteGallery(album.id)
          }
        })

        if (confirmed) {
          this.albums = this.albums.filter(a => a.id !== album.id)
          if (this.selectedAlbum && this.selectedAlbum.id === album.id) {
            this.closeAlbum()
          }
          this.showMessage('Album deleted successfully!', 'success')
        }
      } catch (error) {
        console.error('Error deleting album:', error)
        this.showMessage(error.message || 'Failed to delete album', 'error')
      }
    },

    async confirmDeleteItem(item) {
      try {
        const confirmed = await this.showConfirmDialog({
          type: 'danger',
          title: 'Delete Item',
          message: `Are you sure you want to delete "${item.title}"?`,
          detail: 'This action cannot be undone.',
          confirmText: 'Delete Item',
          cancelText: 'Cancel',
          onConfirm: async () => {
            await GalleryApiService.removeGalleryItem(this.selectedAlbum.id, item.id)
          }
        })

        if (confirmed) {
          this.albumItems = this.albumItems.filter(i => i.id !== item.id)

          const album = this.albums.find(a => a.id === this.selectedAlbum.id)
          if (album) {
            album.items_count = Math.max(0, (album.items_count || 0) - 1)
            this.selectedAlbum.items_count = album.items_count
          }
          this.showMessage('Item deleted successfully!', 'success')
        }
      } catch (error) {
        console.error('Error deleting item:', error)
        this.showMessage(error.message || 'Failed to delete item', 'error')
      }
    },

    // All existing methods remain the same...
    async loadAlbums() {
      this.loading = true
      try {
        const response = await GalleryApiService.getGalleries()

        if (response?.length > 0) {
          const publicCount = response.filter(a => a.is_public).length
          const privateCount = response.filter(a => !a.is_public).length
        }

        this.albums = response || []
      } catch (error) {
        console.error('❌ Error:', error)
        this.showMessage('Failed to load albums', 'error')
      } finally {
        this.loading = false
      }
    },

    async loadAlbumItems(albumId) {
      this.loadingItems = true
      this.albumItems = []
      try {
        // All pages: the API returns only the first 20 items unless told otherwise
        const response = await GalleryApiService.getAllGalleryItems(albumId)
        this.albumItems = response.data || []

        if (this.selectedAlbum && this.albumItems.length !== this.selectedAlbum.items_count) {
          this.selectedAlbum.items_count = this.albumItems.length
          const albumIndex = this.albums.findIndex(a => a.id === albumId)
          if (albumIndex !== -1) {
            this.albums[albumIndex].items_count = this.albumItems.length
          }
        }
      } catch (error) {
        console.error('Error loading album items:', error)
        this.showMessage('Failed to load album items', 'error')
        this.albumItems = []
      } finally {
        this.loadingItems = false
      }
    },

    async createAlbum() {
      if (!this.newAlbum.title.trim()) return

      this.creating = true
      try {
        const response = await GalleryApiService.createGallery(this.newAlbum)
        if (response) {
          this.albums.unshift(response)
          this.showMessage('Album created successfully!', 'success')
          this.cancelCreate()
        }
      } catch (error) {
        console.error('Error creating album:', error)
        this.showMessage(error.message || 'Failed to create album', 'error')
      } finally {
        this.creating = false
      }
    },

    cancelCreate() {
      this.showCreateForm = false
      this.newAlbum = {
        title: '',
        description: '',
        is_public: true,
        cover_image: null,
        cover_image_preview: null
      }
      if (this.$refs.newCoverImageInput) {
        this.$refs.newCoverImageInput.value = ''
      }
    },

    selectAlbum(album) {
      this.selectedAlbum = album
      this.activeTab = 'items'
      this.resetYouTubeForm()
      this.resetVimeoForm() // NEW
      this.loadAlbumItems(album.id)
    },

    closeAlbum() {
      this.selectedAlbum = null
      this.albumItems = []
      this.activeTab = 'items'
      this.resetYouTubeForm()
      this.resetVimeoForm() // NEW
    },

    toggleMenu(albumId) {
      this.activeMenu = this.activeMenu === albumId ? null : albumId
    },

    closeMenus() {
      this.activeMenu = null
    },

    editAlbum(album) {
      this.editingAlbum = {
        ...album,
        cover_image: null,
        cover_image_preview: album.cover_image_url || album.cover_image || null,
        remove_cover_image: false
      }
      this.activeMenu = null
    },

    async saveEdit() {
      if (!this.editingAlbum.title.trim()) return

      this.saving = true
      try {
        const response = await GalleryApiService.updateGallery(this.editingAlbum.id, this.editingAlbum)
        if (response) {
          const index = this.albums.findIndex(a => a.id === this.editingAlbum.id)
          if (index !== -1) {
            this.albums[index] = response
          }
          if (this.selectedAlbum && this.selectedAlbum.id === this.editingAlbum.id) {
            this.selectedAlbum = response
          }
          this.showMessage('Album updated successfully!', 'success')
          this.closeEdit()
        }
      } catch (error) {
        console.error('Error updating album:', error)
        this.showMessage(error.message || 'Failed to update album', 'error')
      } finally {
        this.saving = false
      }
    },

    closeEdit() {
      this.editingAlbum = null
      if (this.$refs.editCoverImageInput) {
        this.$refs.editCoverImageInput.value = ''
      }
    },

    handleCoverImageChange(event, mode) {
      const file = event?.target?.files?.[0]
      if (!file) return

      if (!file.type.startsWith('image/')) {
        this.showMessage('Please select a valid image file for album cover', 'error')
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        this.coverCropperSrc = e.target.result
        this.coverCropperMode = mode
        this.showCoverCropper = true
      }
      reader.readAsDataURL(file)
    },

    handleCoverCropped(base64) {
      // Convert the base64 data URL to a File object for API upload
      const byteString = atob(base64.split(',')[1])
      const mimeType = base64.split(',')[0].split(':')[1].split(';')[0]
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }
      const blob = new Blob([ab], { type: mimeType })
      const file = new File([blob], 'cover_image.jpg', { type: mimeType })

      if (this.coverCropperMode === 'new') {
        this.newAlbum.cover_image = file
        this.newAlbum.cover_image_preview = base64
      } else if (this.coverCropperMode === 'edit' && this.editingAlbum) {
        this.editingAlbum.cover_image = file
        this.editingAlbum.cover_image_preview = base64
      }

      this.showCoverCropper = false
      this.coverCropperSrc = null
      this.coverCropperMode = null
    },

    handleCoverCropperCancel() {
      // Reset the file input so the same file can be re-selected if needed
      if (this.coverCropperMode === 'new' && this.$refs.newCoverImageInput) {
        this.$refs.newCoverImageInput.value = ''
      } else if (this.coverCropperMode === 'edit' && this.$refs.editCoverImageInput) {
        this.$refs.editCoverImageInput.value = ''
      }
      this.showCoverCropper = false
      this.coverCropperSrc = null
      this.coverCropperMode = null
    },

    clearCoverImage(mode) {
      if (mode === 'new') {
        this.newAlbum.cover_image = null
        this.newAlbum.cover_image_preview = null
        if (this.$refs.newCoverImageInput) {
          this.$refs.newCoverImageInput.value = ''
        }
      } else if (this.editingAlbum) {
        this.editingAlbum.cover_image = null
        this.editingAlbum.cover_image_preview = null
        this.editingAlbum.remove_cover_image = true
        if (this.$refs.editCoverImageInput) {
          this.$refs.editCoverImageInput.value = ''
        }
      }
    },

    // Mirrors the backend's generateTitle() fallback, so the pre-filled
    // value matches what would be saved if the uploader leaves it as-is.
    titleFromFilename(filename) {
      const withoutExtension = filename.replace(/\.[^./]+$/, '')
      return withoutExtension
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    },

    // Vimeo Methods
    extractVimeoInfo() {
      const url = this.vimeoVideo.vimeo_url
      if (!url) {
        this.vimeoPreview.videoId = null
        return
      }

      const videoId = this.extractVimeoVideoId(url)
      if (videoId) {
        this.vimeoPreview.videoId = videoId

        if (!this.vimeoVideo.title.trim()) {
          this.vimeoVideo.title = `Vimeo Video - ${videoId}`
        }
      } else {
        this.vimeoPreview.videoId = null
      }
    },

    extractVimeoVideoId(url) {
      // Basic patterns: https://vimeo.com/123456789 or https://vimeo.com/channels/.../123456789
      const match = url.match(/vimeo\.com\/(?:video\/|channels\/[^/]+\/)?(\d+)/)
      return match ? match[1] : null
    },

    async addVimeoVideo() {
      if (!this.canAddVimeoVideo) return

      this.addingVimeo = true
      try {
        const response = await GalleryApiService.addVimeoVideo(this.selectedAlbum.id, this.vimeoVideo)

        if (response) {
          const album = this.albums.find(a => a.id === this.selectedAlbum.id)
          if (album) {
            album.items_count = (album.items_count || 0) + 1
            this.selectedAlbum.items_count = album.items_count
          }

          await this.loadAlbumItems(this.selectedAlbum.id)
          this.showMessage('Vimeo video added successfully!', 'success')
          this.resetVimeoForm()
          this.activeTab = 'items'
        }
      } catch (error) {
        console.error('Error adding Vimeo video:', error)
        this.showMessage(error.message || 'Failed to add Vimeo video', 'error')
      } finally {
        this.addingVimeo = false
      }
    },

    resetVimeoForm() {
      this.vimeoVideo = {
        title: '',
        description: '',
        vimeo_url: ''
      }
      this.vimeoPreview = {
        videoId: null
      }
    },

    // Passed to MultiImageUploader as `upload-handler`. Called once per
    // queued file, concurrently (see MultiImageUploader's `uploadItems`),
    // so a large drop of photos no longer rides on a single all-or-nothing
    // bulk request.
    async handleGalleryFileUpload(file, item, onProgress) {
      const type = file.type.startsWith('video/') ? 'video' : 'image'
      const title = (item?.title && item.title.trim()) || this.titleFromFilename(file.name)

      const response = await GalleryApiService.addGalleryItem(this.selectedAlbum.id, { file, title, type }, { onProgress })

      const album = this.albums.find(a => a.id === this.selectedAlbum.id)
      if (album) {
        album.items_count = (album.items_count || 0) + 1
        this.selectedAlbum.items_count = album.items_count
      }

      return response
    },

    // Passed to MultiImageUploader as the `@batch-complete` handler; fires
    // once every file queued in that batch has either succeeded or failed.
    async handleGalleryBatchComplete({ succeeded, failed }) {
      if (this.selectedAlbum) {
        await this.loadAlbumItems(this.selectedAlbum.id)
      }

      if (succeeded.length > 0) {
        let message = `${succeeded.length} file${succeeded.length !== 1 ? 's' : ''} uploaded successfully!`
        if (failed.length > 0) {
          message += ` (${failed.length} failed)`
        }
        this.showMessage(message, failed.length > 0 ? 'error' : 'success')
        this.activeTab = 'items'
      } else if (failed.length > 0) {
        this.showMessage('Upload failed for all files. Check the errors below and retry.', 'error')
      }
    },

    // YouTube Methods
    extractYouTubeInfo() {
      const url = this.youtubeVideo.youtube_url
      if (!url) {
        this.youtubePreview.videoId = null
        this.youtubePreview.thumbnail = null
        return
      }

      const videoId = this.extractYouTubeVideoId(url)
      if (videoId) {
        this.youtubePreview.videoId = videoId
        this.youtubePreview.thumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`

        if (!this.youtubeVideo.title.trim()) {
          this.youtubeVideo.title = `YouTube Video - ${videoId}`
        }
      } else {
        this.youtubePreview.videoId = null
        this.youtubePreview.thumbnail = null
      }
    },

    extractYouTubeVideoId(url) {
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
      ]

      for (const pattern of patterns) {
        const match = url.match(pattern)
        if (match) return match[1]
      }
      return null
    },

    async addYouTubeVideo() {
      if (!this.canAddYouTubeVideo) return

      this.addingYouTube = true
      try {
        const response = await GalleryApiService.addYouTubeVideo(this.selectedAlbum.id, this.youtubeVideo)

        if (response) {
          const album = this.albums.find(a => a.id === this.selectedAlbum.id)
          if (album) {
            album.items_count = (album.items_count || 0) + 1
            this.selectedAlbum.items_count = album.items_count
          }

          await this.loadAlbumItems(this.selectedAlbum.id)
          this.showMessage('YouTube video added successfully!', 'success')
          this.resetYouTubeForm()
          this.activeTab = 'items'
        }
      } catch (error) {
        console.error('Error adding YouTube video:', error)
        this.showMessage(error.message || 'Failed to add YouTube video', 'error')
      } finally {
        this.addingYouTube = false
      }
    },

    resetYouTubeForm() {
      this.youtubeVideo = {
        title: '',
        description: '',
        youtube_url: ''
      }
      this.youtubePreview = {
        videoId: null,
        thumbnail: null
      }
    },

    getAlbumThumbnail(album) {
      if (album.cover_image_url) {
        return album.cover_image_url
      }
      if (album.cover_image) {
        return album.cover_image
      }
      if (album.latest_item_thumbnail) {
        return album.latest_item_thumbnail
      }
      return album.thumbnail_url || this.defaultThumbnail
    },

    getItemThumbnail(item) {
      return item.thumbnail_url || item.file_url || this.defaultThumbnail
    },

    getItemCount(album) {
      return album.items_count || 0
    },

    handleImageError(event) {
      event.target.src = this.defaultThumbnail
    },

    formatDate(dateString) {
      return GalleryApiService.formatDate(dateString)
    },

    showMessage(text, type = 'success') {
      this.message = text
      this.messageType = type
      setTimeout(() => {
        this.message = ''
      }, 3000)
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.simple-gallery-page {
  margin: 0 auto;
  padding: 2rem;
  font-family: var(--font-family-base);
}

/* Header */
.page-header {
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-header h1 {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  color: #1a1a1a;
  margin: 0;
}

.new-album-btn {
  background: #009CAD;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background 0.2s ease;
}

.new-album-btn:hover {
  background: #008a9a;
}

.cover-preview {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
}

.cover-preview-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

/* Create Album Modal */
.create-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 65;
  padding: 1rem;
}

.create-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: modalSlideIn 0.2s ease-out;
}

.create-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.create-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: #1a1a1a;
}

.create-body {
  overflow-y: auto;
  padding: 1.5rem;
}

.create-actions {
  border-top: 1px solid #e5e7eb;
  margin-top: 0;
  padding: 1rem 1.5rem 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group > label {
  display: block;
  font-weight: var(--font-weight-medium);
  color: #374151;
  margin-bottom: 0.5rem;
}

.album-public-toggle {
  margin-top: 0.25rem;
}

.album-public-toggle .form-check {
  margin-bottom: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-left: 0;
  min-height: 0;
}

.album-public-toggle .form-check-input {
  appearance: auto;
  -webkit-appearance: checkbox;
  display: inline-block;
  width: 16px;
  height: 16px;
  min-width: 16px;
  padding: 0;
  margin: 0;
  float: none;
  border: initial;
  border-radius: initial;
  background: initial;
  background-image: none;
}

.album-public-toggle .form-check-label {
  margin: 0 !important;
  display: inline-block !important;
  position: static !important;
  top: auto !important;
  left: auto !important;
  padding: 0 !important;
  background: transparent !important;
  color: #374151 !important;
  font-size: var(--font-size-sm) !important;
  line-height: var(--line-height-tight) !important;
  font-weight: var(--font-weight-medium) !important;
  transform: none !important;
}

.album-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: var(--font-size-base);
  transition: border-color 0.2s ease;
}

.album-input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.album-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: var(--font-size-base);
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease;
}

.album-textarea:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.btn-primary {
  background: #0ea5e9;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  background: #0284c7;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

/* Loading */
.loading-state {
  text-align: center;
  padding: 4rem 2rem;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f4f6;
  border-top: 3px solid #0ea5e9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

.spinner-small {
  width: 24px;
  height: 24px;
  border: 2px solid #f3f4f6;
  border-top: 2px solid #0ea5e9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 0.5rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Albums Grid */
.albums-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
}

.album-card {
  position: relative;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
}

.album-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.album-thumbnail {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.album-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.album-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.album-card:hover .album-overlay {
  opacity: 1;
}

.upload-btn {
  background: #009CAD;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.2s ease;
}

.upload-btn:hover {
  background: #008a9a;
}

.album-info {
  padding: 1.5rem;
}

.album-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.album-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--font-size-sm);
  color: #6b7280;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.public-badge {
  background: #10b981;
  color: white;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.private-badge {
  background: #6b7280;
  color: white;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.album-menu {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.album-menu:hover {
  background: white;
}

.album-dropdown {
  position: absolute;
  top: 3rem;
  right: 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: 120px;
}

.album-dropdown button {
  width: 100%;
  padding: 0.75rem;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;
}

.album-dropdown button:hover {
  background: #f3f4f6;
}

.album-dropdown button.delete-option {
  color: #dc2626;
}

.album-dropdown button.delete-option:hover {
  background: #fef2f2;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.empty-icon {
  margin-bottom: 1.5rem;
  color: #9ca3af;
}

.empty-state h3 {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  color: #6b7280;
  margin: 0 0 2rem 0;
}

.empty-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  border-radius: 15px 15px 0 0;
  padding: 1.5rem;
}

.card-title {
  font-weight: var(--font-weight-semibold);
}

.card-body {
  padding: 1.5rem;
}

/* Album Management Modal */
.album-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 1rem;
}

.album-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.album-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.album-title-section h2 {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.album-meta-info {
  display: flex;
  gap: 1rem;
  align-items: center;
  font-size: var(--font-size-sm);
  color: #6b7280;
}

.close-btn {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.close-btn:hover {
  background: #f3f4f6;
}

/* Tab Navigation */
.tab-navigation {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 2rem;
}

.tab-btn {
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  font-weight: var(--font-weight-medium);
  color: #6b7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
}

.tab-btn:hover {
  color: #1a1a1a;
}

.tab-btn.active {
  color: #0ea5e9;
  border-bottom-color: #0ea5e9;
}

/* Items Tab */
.items-tab {
  padding: 2rem;
  flex: 1;
}

.loading-items {
  text-align: center;
  padding: 3rem 1rem;
}

.empty-items {
  text-align: center;
  padding: 3rem 1rem;
}

.empty-items p {
  color: #6b7280;
  margin-bottom: 1rem;
}

.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
}

.item-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.item-image {
  position: relative;
  width: 100%;
  height: 150px;
  overflow: hidden;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.item-card:hover .item-overlay {
  opacity: 1;
}

.item-action-btn {
  background: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin: 0 0.25rem;
}

.delete-btn:hover {
  background: #fee2e2;
  color: #dc2626;
}

.item-info {
  padding: 1rem;
}

.item-title {
  font-weight: var(--font-weight-medium);
  color: #1a1a1a;
  font-size: var(--font-size-sm);
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.5rem;
}

.item-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--font-size-xs);
  color: #6b7280;
}

.item-type {
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
}

.item-type.image {
  background: #dcfce7;
  color: #166534;
}

.item-type.video {
  background: #dbeafe;
  color: #1e40af;
}

/* Upload Tab */
.upload-tab {
  padding: 2rem;
  flex: 1;
}

.gallery-item-title-input {
  display: block;
  width: 100%;
  margin-top: 0.35rem;
  padding: 0.25rem 0.5rem;
  font-size: var(--font-size-sm);
  border: 1px solid #d1d5db;
  border-radius: 4px;
}

/* YouTube Tab */
.youtube-tab {
  padding: 2rem;
  flex: 1;
}

.youtube-form {
  max-width: 600px;
}

.youtube-form h3 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: #1a1a1a;
  margin: 0 0 1.5rem 0;
}

.youtube-preview {
  margin: 1.5rem 0;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.youtube-preview h4 {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

.preview-container {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.preview-thumbnail {
  width: 160px;
  height: 90px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.preview-info {
  flex: 1;
}

.preview-info h5 {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.preview-info p {
  font-size: var(--font-size-sm);
  color: #6b7280;
  margin: 0;
}

/* Edit Modal */
.edit-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  padding: 2rem;
}

.edit-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  padding: 2rem;
  max-height: 90vh;
  overflow-y: auto;
}

.edit-content h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: #1a1a1a;
  margin: 0 0 1.5rem 0;
}

/* Confirmation Dialog Styles */
.confirmation-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.confirmation-dialog {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: modalSlideIn 0.2s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.confirmation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 0;
}

.confirmation-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: #1a1a1a;
  margin: 0;
}

.confirmation-close {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s ease;
}

.confirmation-close:hover:not(:disabled) {
  background: #f3f4f6;
  color: #1a1a1a;
}

.confirmation-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.confirmation-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.confirmation-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.confirmation-icon.info {
  background: #e0f2fe;
  color: #0369a1;
}

.confirmation-icon.warning {
  background: #fef3c7;
  color: #d97706;
}

.confirmation-icon.danger {
  background: #fee2e2;
  color: #dc2626;
}

.confirmation-message {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
  line-height: var(--line-height-normal);
}

.confirmation-detail {
  font-size: var(--font-size-sm);
  color: #6b7280;
  margin: 0;
  line-height: var(--line-height-normal);
}

.confirmation-actions {
  display: flex;
  gap: 0.75rem;
  padding: 0 1.5rem 1.5rem;
  justify-content: flex-end;
}

.confirmation-cancel,
.confirmation-confirm {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
  position: relative;
}

.confirmation-cancel {
  background: #f9fafb;
  color: #374151;
  border: 1px solid #d1d5db;
}

.confirmation-cancel:hover:not(:disabled) {
  background: #f3f4f6;
}

.confirmation-confirm {
  background: #0ea5e9;
  color: white;
  border: 1px solid #0ea5e9;
}

.confirmation-confirm:hover:not(:disabled) {
  background: #0284c7;
}

.confirmation-confirm.btn-danger {
  background: #dc2626;
  border-color: #dc2626;
}

.confirmation-confirm.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
}

.confirmation-confirm.btn-warning {
  background: #d97706;
  border-color: #d97706;
}

.confirmation-confirm.btn-warning:hover:not(:disabled) {
  background: #b45309;
}

.confirmation-confirm:disabled,
.confirmation-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 0.5rem;
}

/* Toast Messages */
.toast {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  font-weight: var(--font-weight-medium);
  z-index: 100;
  animation: slideInRight 0.3s ease-out;
}

.toast.success {
  background: #10b981;
  color: white;
}

.toast.error {
  background: #ef4444;
  color: white;
}

/* Animations */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .simple-gallery-page {
    padding: 1rem;
  }

  .header-content {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .albums-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .create-modal {
    padding: 0.5rem;
  }

  .create-content {
    max-height: 95vh;
  }

  .create-header,
  .create-body,
  .create-actions {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .album-modal {
    padding: 0.5rem;
  }

  .album-content {
    max-height: 95vh;
  }

  .album-header {
    padding: 1.5rem 1rem 1rem;
  }

  .tab-navigation {
    padding: 0 1rem;
  }

  .items-tab,
  .upload-tab,
  .youtube-tab {
    padding: 1rem;
  }

  .items-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
  }

  .form-actions,
  .empty-actions {
    flex-direction: column;
  }

  .toast {
    bottom: 1rem;
    right: 1rem;
    left: 1rem;
  }

  .preview-container {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }

  .preview-thumbnail {
    width: 100%;
    height: auto;
    max-width: 280px;
  }

  .youtube-form {
    max-width: 100%;
  }

  .confirmation-dialog {
    margin: 1rem;
    max-width: none;
  }

  .confirmation-actions {
    flex-direction: column;
  }

  .confirmation-cancel,
  .confirmation-confirm {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: var(--font-size-xl);
  }

  .album-info {
    padding: 1rem;
  }

  .album-title {
    font-size: var(--font-size-base);
  }

  .album-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .close-btn {
    align-self: flex-end;
  }

  .items-grid {
    grid-template-columns: 1fr;
  }

  .tab-btn {
    padding: 0.75rem 1rem;
    font-size: var(--font-size-sm);
  }
}
</style>
